// src/com/sms_phrases/SmsPhrases.js
import React, { useEffect, useRef, useState } from 'react';
import { Box } from '@mui/system';
//import { styled } from '@mui/material/styles';
import { Button, IconButton, CircularProgress } from '@mui/material';
import CachedIcon from '@mui/icons-material/Cached'; // refresh
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import EditIcon from '@mui/icons-material/Edit';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import SecurityUpdateGoodIcon from '@mui/icons-material/SecurityUpdateGood'; // use this phrase
import { useLazyQuery, useMutation, useReactiveVar } from '@apollo/client';
import { useSnackbar } from 'notistack';
import SearchBox from '../../common/SearchBox';
import {
    ContentConainer, ContentHeader, ControlBox, CountLabel, ToolBox
} from '../../common/TopViewControl';
import { useConfirmDialog } from '../../common/AppConfirmDialog';
import { AppObject, AppStyle, AppWord } from '../../value/AppConst';
import { authTableTreeRepo, smsPhrasesListRepo } from '../../value/AppModel';
import {
    LIST_SMS_PHRASES,
    ADD_SMS_PHRASES, EDIT_SMS_PHRASES, REMOVE_SMS_PHRASES
} from './SmsPhrasesGql';
import { SmsPhrasesDictionary } from './SmsPhrasesDict';
import { useAlertSetter, useAuthorizationCheck } from '../../api/app-hook';
import FlexyTable from '../../common/flexytable/FlexyTable';
import VzmTextField from '../../common/VzmTextField';
import InputUtil from '../../value/InputUtil';

const InputMap = SmsPhrasesDictionary.inputMap;

/**
 * (SMS_PHRASES) 정보 View Controller
 * SMS 메시지를 보낼때 자주 사용하는 문구
 * Using React with MUI5
 * Project: NewTOP for cloud
 * 2025-01-24 15:30:53 using DbdbTap
 * https://www.dbdbtap.com
 */
export default function SmsPhrases({
    onSelected, // Callback when a phrase is selected.
}) {
    const authTableTree = useReactiveVar(authTableTreeRepo);
    //const [editing, setEditing] = useState(false);
    const [input, setInput] = useState({...AppObject.emptyInputData});
    const [isEdit, setIsEdit] = useState(false);
    const smsPhrasesListData = useReactiveVar(smsPhrasesListRepo);
    const [searchText, setSearchText] = useState('');
    const [countToDisplay, setCountToDisplay] = useState(0);
    const openConfirmDialog = useConfirmDialog();
    const checkAuth = useAuthorizationCheck();
    const addAlert = useAlertSetter();
    const { enqueueSnackbar } = useSnackbar();
    const lastReloadRef = useRef(0);

    // ##### Call GraphQL to get List #####
    const [getSmsPhrasesList, responseList] = useLazyQuery(LIST_SMS_PHRASES, {
        ...AppObject.NoCachedFetch,
        onCompleted: (data, option) => {onCompleteGetList(data, option)},
		onError: (error) => addAlert({title: SmsPhrasesDictionary.errorTitle.Get, message: [error.message]})
    });

    // ##### GraphQL Mutation. 'onError' for prevent from printing error at console (Uncaught exception) ###
    const [addSmsPhrases, responseAdd] = useMutation( ADD_SMS_PHRASES, {
		onCompleted: (data, option) => onCompleteAdd(data, option), 
		onError: (error) => addAlert({title: SmsPhrasesDictionary.errorTitle.Add, message: [error.message]})
	} );
    const [editSmsPhrases, responseEdit] = useMutation( EDIT_SMS_PHRASES, {
		onCompleted: (data, option) => onCompleteEdit(data, option),
		onError: (error) => addAlert({title: SmsPhrasesDictionary.errorTitle.Edit, message: [error.message]})
	} );
    const [removeSmsPhrases, responseRemove] = useMutation( REMOVE_SMS_PHRASES, {
		onCompleted: (data, option) => onCompleteRemove(data, option),
		onError: (error) => addAlert({title: SmsPhrasesDictionary.errorTitle.Remove, message: [error.message]})
	});

    const getSmsPhrasesListNow = () => {
        getSmsPhrasesList();
        lastReloadRef.current = new Date().getTime()/1000;
    };

    useEffect(() => {
        const diff = new Date().getTime()/1000 - smsPhrasesListData.when;
        if(diff > 1200) //  || smsPhrasesListData.records.length === 0 - Don't use this condition
            getSmsPhrasesListNow();
    }, []);


    // >>>>>>>>> callbacks <<<<<<<<<<<<<
    const onCompleteGetList = (data, clientOption) => {
        if(data.smsPhrasesList) {
            smsPhrasesListRepo({when:new Date().getTime()/1000, records:data.smsPhrasesList});
        }
    };

    const clearEditor = () => {
        const newInput = {...AppObject.emptyInputData};
        newInput.data.phrase = '';
        setInput(newInput);
        //setEditing(false);
        setIsEdit(false);
    };

    const onCompleteAdd = (data, clientOption) => {
        if(data.smsPhrasesAdd.ok) {
            clearEditor();
            getSmsPhrasesListNow();
        }
        else addAlert({title:SmsPhrasesDictionary.errorTitle.Add, message:[data.smsPhrasesAdd.message]});
    };

    const onCompleteEdit = (data, clientOption) => {
        if(data.smsPhrasesEdit.ok) {
            clearEditor();
            getSmsPhrasesListNow();
        }
        else addAlert({title:SmsPhrasesDictionary.errorTitle.Edit, message:[data.smsPhrasesEdit.message]});
    };

    const onCompleteRemove = (data, clientOption) => {
        if(data.smsPhrasesRemove.ok) getSmsPhrasesListNow();
        else addAlert({title:SmsPhrasesDictionary.errorTitle.Remove, message:[data.smsPhrasesRemove.message]});
    };

    // >>>>>>>>> Event Handler <<<<<<<<<<<<<

    const onClickReload = () => {
        const diff = new Date().getTime()/1000 - lastReloadRef.current;
        if(diff < 60) {
            // Too short to reload
            enqueueSnackbar(AppWord.ReloadNotInOneMinute, {variant:'info', autoHideDuration:2000});
        }
        else getSmsPhrasesListNow();
    };

    const onClickSave = () => {
        if(input.data.phrase && input.data.phrase.length >= SmsPhrasesDictionary.inputMap.phrase.min) {
            const smsPhrases = {
                phrase: input.data.phrase,
            };
            if(isEdit) smsPhrases.smsPhrasesSeq = input.data.smsPhrasesSeq;
            const mutateParam = {variables:{smsPhrases:smsPhrases}};

            if(isEdit) editSmsPhrases(mutateParam);
            else addSmsPhrases(mutateParam);
        }
    };

    const onEnterKey = () => {
        onClickSave();
    }

    const onClickAdd = () => {
        //setEditing(true);
        const newInput = {...AppObject.emptyInputData};
        newInput.data = {phrase:''};
        setInput(newInput);
        setIsEdit(false);
    };

    const onRequestEdit = (item) => {
        //setEditing(true);
        const newInput = {...AppObject.emptyInputData};
        newInput.data = {phrase:item.phrase, smsPhrasesSeq:item.smsPhrasesSeq};
        setInput(newInput);
        setIsEdit(true);
    };

    const confirmRemove = async (item) => {
        const confirm = await openConfirmDialog({
            title: "자주쓰는 문구 삭제",
            content: [
                '해당 문구를 삭제합니다.',
                '삭제 후 복구할 수 없습니다. 계속하시겠습니까?'
            ]
        });

        if(confirm)
            removeSmsPhrases({variables:{smsPhrases:{smsPhrasesSeq:item.smsPhrasesSeq}}});
    };

    const onChangeSearchText = (text) => {
        setSearchText(text);
    };

    const onChangeValue = (id, value, error) => {
        const newInput = InputUtil.reflectData(input, id, value, error, InputMap, isEdit);
        setInput(newInput);
    };

    const onClickToolOnRow = (toolId, row) => {
        switch(toolId) {
            case AppObject.command.DELETE: confirmRemove(row); break;
            case AppObject.command.EDIT: onRequestEdit(row); break;
            case AppObject.command.REUSE: onSelected(row.phrase); break;
            default: return;
        }
    };

    const canAdd = checkAuth(authTableTree.tree.pr_sms_phrases_add);
    const canEdit = checkAuth(authTableTree.tree.pr_sms_phrases_edit);
    const canRemove = checkAuth(authTableTree.tree.pr_sms_phrases_remove);
    const tools = [
        {id: AppObject.command.REUSE, icon:<SecurityUpdateGoodIcon/>}
    ];

    if(canEdit) tools.push({id:AppObject.command.EDIT, icon:<EditIcon/>});
    if(canRemove) tools.push({id:AppObject.command.DELETE, icon:<DeleteForeverIcon/>, color: AppStyle.mui.warning});

    const loadingAny
        = responseList.loading
        || responseAdd.loading
        || responseEdit.loading
        || responseRemove.loading;

    return(
        <ContentConainer style={{border: AppStyle.BorderCCC, borderRadius: 5, paddingBottom: 10}}>
            <ContentHeader style={{minWidth:640}}>
                <ControlBox>
                    <SearchBox
                        value={searchText}
                        onChangeText={onChangeSearchText}
                        style={{maxWidth:140}}
                    />
                    <span style={{
                        marginLeft:10, color:AppStyle.PrimaryRGB, fontSize:'0.85rem'
                    }}>
                        더블클릭 또는 폰 아이콘 클릭해서 문구 적용.
                    </span>
                </ControlBox>
                <ToolBox>
                    <CountLabel count={countToDisplay} />
                    {
                        loadingAny ?
                        <CircularProgress size={20} />
                        :
                        <IconButton
                            color={AppStyle.mui.inherit}
                            onClick={onClickReload}
                        >
                            <CachedIcon />
                        </IconButton>
                    }
                    {
                        canAdd &&
                        <IconButton
                            color={AppStyle.mui.primary}
                            style={{marginLeft:10}}
                            onClick={onClickAdd}
                            disabled={
                                loadingAny
                            }
                        >
                            <NoteAddIcon />
                        </IconButton>
                    }
                </ToolBox>
            </ContentHeader>
            <FlexyTable
                name={SmsPhrasesDictionary.tableProps.tableId}
                tableProps={SmsPhrasesDictionary.tableProps}
                records={smsPhrasesListData.records}
                search={searchText}
                tools={tools}
                onClickTool={onClickToolOnRow}
                onChangeCountToDisplay={setCountToDisplay}
                onRowDoubleClicked={(row)=>onSelected(row.phrase)}
                usePopoverBubble={true}
            />
            <Box style={{display:'flex', alignItems:'center', width:'100%', marginTop:10}}>
                <Box style={{flexGrow:1}}>
                    <VzmTextField
                        dict={SmsPhrasesDictionary.inputMap.phrase}
                        value={input.data.phrase}
                        onChange={onChangeValue}
                        fullWidth={true}
                        onEnterKey={onEnterKey}
                    />
                </Box>
                <Button
                    onClick={onClickSave}
                    variant={AppStyle.mui.contained}
                    color={isEdit ? AppStyle.mui.secondary : AppStyle.mui.primary}
                    style={{marginLeft:10}}
                >
                    {isEdit ? '수정' : '추가'}
                </Button>
            </Box>
        </ContentConainer>
    );
}
